import type {FarmState} from './model';

export interface FarmCopyDifference {
  collection: string;
  key: string;
  label: string;
  kind: 'local-only' | 'remote-only' | 'changed';
}
export interface FarmCopyComparison {
  equivalent: boolean;
  onlyPrintAcknowledgements: boolean;
  differences: FarmCopyDifference[];
}

const isObject = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const own = (value: object, key: string) => Object.prototype.hasOwnProperty.call(value, key);

/** Compare complete JSON values without treating object property order as a data change. */
export function sameFarmValue(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false;
    const keys = Object.keys(left);
    const leftValues = left as unknown as Record<string, unknown>, rightValues = right as unknown as Record<string, unknown>;
    return keys.length === Object.keys(right).length && keys.every(key => own(right, key) && sameFarmValue(leftValues[key], rightValues[key]));
  }
  if (!isObject(left) || !isObject(right)) return false;
  if (![Object.prototype, null].includes(Object.getPrototypeOf(left)) || ![Object.prototype, null].includes(Object.getPrototypeOf(right))) return false;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every(key => own(right, key) && sameFarmValue(left[key], right[key]));
}

const printCollections = ['cerdas', 'verracos', 'lotes'] as const;
const titles: Record<string, string> = {
  cerdas: 'Cerdas', verracos: 'Verracos', lotes: 'Lotes', montas: 'Montas', celos: 'Celos',
  repeticiones: 'Repeticiones', partos: 'Partos', camadas: 'Camadas', espacios: 'Espacios',
  espaciosCerdas: 'Espacios de cerdas', alimentacion: 'Alimentación', racionesLactancia: 'Raciones de lactancia',
  pesajes: 'Pesajes', mortalidad: 'Mortalidad', fasesAlimento: 'Fases de alimento',
  transferenciasLechones: 'Transferencias de lechones', tareasManual: 'Tareas manuales',
  procedimientos: 'Procedimientos', aplicacionesProcedimientos: 'Aplicaciones de procedimientos',
  tareasRealizadas: 'Tareas realizadas', tareasHechas: 'Tareas terminadas', eventOverrides: 'Programación de tareas',
  configuracion: 'Configuración', counters: 'Contadores', protocolos: 'Protocolos',
  'protocolos.reproduccion': 'Protocolos · Reproducción', 'protocolos.posparto': 'Protocolos · Maternidad',
  'protocolos.lechones': 'Protocolos · Lechones', 'protocolos.lote': 'Protocolos · Lotes',
  'protocolos.reemplazoCerda': 'Protocolos · Reemplazo de cerdas',
  'protocolos.reemplazoVerraco': 'Protocolos · Reemplazo de verracos',
};
const title = (collection: string) => titles[collection] || collection;

function identities(rows: unknown[], collection: string): string[] | null {
  const field = collection === 'cerdas' ? 'arete' : 'id';
  const keys: string[] = [];
  for (const row of rows) {
    if (!isObject(row) || typeof row[field] !== 'string' || !row[field]) return null;
    keys.push(row[field]);
  }
  return new Set(keys).size === keys.length ? keys : null;
}

function withoutPrintReminder(row: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(row).filter(([key]) => key !== 'imprimirPendiente' && key !== 'motivoImpresion'));
}

/** Only explicit acknowledgement of an existing reminder is safe to recognize without a merge. */
function onlyAcknowledgements(local: FarmState, remote: FarmState): boolean {
  const left = local as unknown as Record<string, unknown>, right = remote as unknown as Record<string, unknown>;
  if (!sameFarmValue(Object.keys(left).sort(), Object.keys(right).sort())) return false;
  let acknowledged = false;
  for (const collection of printCollections) {
    if (!Array.isArray(left[collection]) || !Array.isArray(right[collection])
      || !identities(left[collection], collection) || !identities(right[collection], collection)) return false;
  }
  for (const key of Object.keys(left)) {
    if (!(printCollections as readonly string[]).includes(key)) {
      if (!sameFarmValue(left[key], right[key])) return false;
      continue;
    }
    const localRows = left[key] as Record<string, unknown>[], remoteRows = right[key] as Record<string, unknown>[];
    if (localRows.length !== remoteRows.length) return false;
    for (let index = 0; index < localRows.length; index++) {
      const l = localRows[index], r = remoteRows[index];
      if (sameFarmValue(l, r)) continue;
      if (!sameFarmValue(withoutPrintReminder(l), withoutPrintReminder(r))) return false;
      if (l.imprimirPendiente !== false || l.motivoImpresion !== '') return false;
      if (r.imprimirPendiente !== undefined && typeof r.imprimirPendiente !== 'boolean') return false;
      if (r.motivoImpresion !== undefined && typeof r.motivoImpresion !== 'string') return false;
      if (r.imprimirPendiente !== true && !(typeof r.motivoImpresion === 'string' && r.motivoImpresion.length > 0)) return false;
      acknowledged = true;
    }
  }
  return acknowledged;
}

function rowLabel(collection: string, key: string, row: unknown, positional = false): string {
  if (positional) return `${title(collection)} · fila ${Number(key) + 1}`;
  if (collection === 'cerdas') return `Cerda ${key}`;
  if (collection === 'verracos') return `Verraco ${isObject(row) && typeof row.nombre === 'string' && row.nombre ? row.nombre : key}`;
  if (collection === 'lotes') return `Lote ${key}`;
  return `${title(collection)} · ${key}`;
}

/** Summaries are descriptive only. They never select a winner or discard unknown fields. */
export function compareFarmCopies(local: FarmState, remote: FarmState): FarmCopyComparison {
  if (sameFarmValue(local, remote)) return {equivalent: true, onlyPrintAcknowledgements: false, differences: []};
  const differences: FarmCopyDifference[] = [];
  const compare = (left: unknown, right: unknown, collection: string, leftExists = true, rightExists = true) => {
    if (leftExists && rightExists && sameFarmValue(left, right)) return;
    if (leftExists && rightExists && Array.isArray(left) && Array.isArray(right)) {
      const leftIds = identities(left, collection), rightIds = identities(right, collection);
      const stable = leftIds !== null && rightIds !== null;
      const lKeys = stable ? leftIds : left.map((_, index) => String(index));
      const rKeys = stable ? rightIds : right.map((_, index) => String(index));
      const lMap = new Map(lKeys.map((key, index) => [key, left[index]]));
      const rMap = new Map(rKeys.map((key, index) => [key, right[index]]));
      for (const key of new Set([...lKeys, ...rKeys])) {
        const lHas = lMap.has(key), rHas = rMap.has(key);
        if (lHas && rHas && sameFarmValue(lMap.get(key), rMap.get(key))) continue;
        differences.push({collection, key, label: rowLabel(collection, key, lMap.get(key) ?? rMap.get(key), !stable), kind: !rHas ? 'local-only' : !lHas ? 'remote-only' : 'changed'});
      }
      if (stable && lKeys.length === rKeys.length && lKeys.every(key => rMap.has(key)) && !sameFarmValue(lKeys, rKeys)) {
        differences.push({collection, key: '$order', label: `Orden de ${title(collection)}`, kind: 'changed'});
      }
      return;
    }
    if (leftExists && rightExists && isObject(left) && isObject(right)) {
      for (const key of new Set([...Object.keys(left), ...Object.keys(right)])) {
        if (collection === 'protocolos') compare(left[key], right[key], `protocolos.${key}`, own(left, key), own(right, key));
        else if (!own(left, key) || !own(right, key) || !sameFarmValue(left[key], right[key])) differences.push({collection, key, label: `${title(collection)} · ${key}`, kind: !own(right, key) ? 'local-only' : !own(left, key) ? 'remote-only' : 'changed'});
      }
      return;
    }
    differences.push({collection, key: '$', label: title(collection), kind: !rightExists ? 'local-only' : !leftExists ? 'remote-only' : 'changed'});
  };
  const left = local as unknown as Record<string, unknown>, right = remote as unknown as Record<string, unknown>;
  for (const collection of new Set([...Object.keys(left), ...Object.keys(right)])) compare(left[collection], right[collection], collection, own(left, collection), own(right, collection));
  return {equivalent: false, onlyPrintAcknowledgements: onlyAcknowledgements(local, remote), differences};
}
